import React, { useState, useEffect } from 'react';
import { db, rtdb } from '../firebase-config';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ref, get, child } from 'firebase/database';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Container,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import QuizIcon from '@mui/icons-material/Quiz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CreateIcon from '@mui/icons-material/Create';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import LatexRenderer from './LatexRenderer';
import { EXAM_TYPES, subjectColor } from '../utils/enums';

function Dashboard({ onPracticeClick, onStartExamClick }) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Filters live in the URL (?type=NEET&subject=Physics) so Home's cards and shared links can preset them
  const examTypeFilter = searchParams.get('type');
  const subjectFilter = searchParams.get('subject');
  const setFilter = (key, value) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Shared exam links: /scheduled-exams/<examId> opens that exam directly
  const { examId: linkedExamId } = useParams();
  const navigate = useNavigate();
  const [linkError, setLinkError] = useState(null);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        let examsList = [];
        let templatesList = [];

        try {
          const examsSnap = await getDocs(query(collection(db, 'exams'), orderBy('examDateTime', 'asc')));
          examsList = examsSnap.docs.map(doc => ({ docId: doc.id, ...doc.data() }));
        } catch (e) { console.error("Firestore Exams Error:", e); }

        if (examsList.length === 0) {
          const examsSnap = await get(child(ref(rtdb), 'exams'));
          if (examsSnap.exists()) {
            const data = examsSnap.val();
            examsList = Array.isArray(data) 
              ? data.filter(Boolean).map((e, i) => ({ docId: String(i), ...e }))
              : Object.entries(data).map(([id, val]) => ({ docId: id, ...val }));
          }
        }

        try {
          const tSnap = await getDocs(collection(db, 'examTemplates'));
          templatesList = tSnap.docs.map(doc => ({ docId: doc.id, ...doc.data() }));
        } catch (e) { console.error("Firestore Templates Error:", e); }

        if (templatesList.length === 0) {
          const paths = ['exam_templates', 'examTemplates', 'templates'];
          for (const path of paths) {
            const tSnap = await get(child(ref(rtdb), path));
            if (tSnap.exists()) {
              const data = tSnap.val();
              templatesList = Array.isArray(data)
                ? data.filter(Boolean).map((t, i) => ({ docId: String(i), ...t }))
                : Object.entries(data).map(([id, val]) => ({ docId: id, ...val }));
              break;
            }
          }
        }

        const templateMap = {};
        templatesList.forEach(t => {
          if (t.docId) templateMap[t.docId] = t;
          if (t.id) templateMap[String(t.id)] = t;
        });

        // Exams switched off in the admin app are hidden, including from shared links
        const enrichedExams = examsList.filter(e => e.isVisible !== false).map(e => {
          const refId = e.templateId || e.examTemplateId || e.id || e.examType;
          let template = templateMap[String(refId)];
          if (!template) {
            template = Object.values(templateMap).find(t =>
              (t.name && e.name && t.name === e.name) ||
              (t.name && e.examType && t.name === e.examType) ||
              (t.id && e.examTemplateId && String(t.id) === String(e.examTemplateId))
            );
          }
          if (template) {
            const updatedExam = { ...e };
            if (Array.isArray(template.subjects)) {
              if (!e.duration || String(e.duration).toUpperCase() === 'N/A') {
                updatedExam.duration = template.subjects.reduce((sum, s) => sum + (parseInt(s.duration || s.time || 0) || 0), 0);
              }
              if (!e.totalQuestions) {
                updatedExam.totalQuestions = template.subjects.reduce((sum, s) => sum + (parseInt(s.numQuestions || s.totalQuestions || s.questions || 0) || 0), 0);
              }
            }
            // Fallback: copy examType from template for exams created before this field was added
            if (!e.examType && template.examType) updatedExam.examType = template.examType;
            return updatedExam;
          }
          return e;
        });
        setExams(enrichedExams);
      } catch (err) {
        console.error(err);
        setError('Failed to load scheduled exams.');
      } finally {
        setLoading(false);
      }
    };
    fetchExams();
  }, []);

  useEffect(() => {
    if (!linkedExamId || loading) return;
    const exam = exams.find(e => e.docId === linkedExamId || String(e.id) === linkedExamId);
    // Drop the id from the address first, so Back from the exam returns to the list instead of reopening it
    navigate('/scheduled-exams', { replace: true });
    if (exam) onStartExamClick(exam);
    else setLinkError("This exam link isn't valid any more. The exam may have been removed — pick one below.");
    // Run once per link, when the list has loaded; other deps would reopen the exam on re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedExamId, loading]);

  const getSubjectList = (subjects) => {
    if (!subjects) return [];
    if (Array.isArray(subjects)) {
      return subjects.map((s, i) => ({
        name: s.name || `Subject ${i + 1}`,
        questions: Array.isArray(s) ? s.length : (s.questions || s.totalQuestions || 0)
      }));
    }
    if (typeof subjects === 'object') {
      return Object.entries(subjects).map(([key, val]) => ({
        name: key,
        questions: Array.isArray(val) ? val.length : (typeof val === 'object' ? (val.questions || val.totalQuestions || 0) : val)
      }));
    }
    return [];
  };

  const getStatusInfo = (examDateTime) => {
    if (!examDateTime) return { label: 'Upcoming', color: '#64748b', bgcolor: '#f1f5f9' };
    const examDate = examDateTime.toDate ? examDateTime.toDate() : new Date(examDateTime);
    const now = new Date();
    const diffMs = examDate - now;
    if (diffMs < 0) return { label: 'Completed', color: '#10b981', bgcolor: '#ecfdf5' };
    if (diffMs / (1000 * 60 * 60) < 24) return { label: 'Today', color: '#f59e0b', bgcolor: '#fffbeb' };
    return { label: 'Upcoming', color: '#3b82f6', bgcolor: '#eff6ff' };
  };

  const renderExamCard = (exam) => {
    const subjectList = getSubjectList(exam.subjects);
    const status = getStatusInfo(exam.examDateTime);
    return (
      <Card key={exam.docId} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: '24px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '16px' }}>
            <Box>
              <Chip label={status.label} size="small" sx={{
                bgcolor: status.bgcolor,
                color: status.color,
                fontWeight: 700,
                fontSize: '10px',
                mb: '8px',
                borderRadius: '4px'
              }} />
              <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '18px' }}>
                <LatexRenderer text={exam.id || exam.docId || 'Unnamed Exam'} />
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: '24px' }}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TimerIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                <Box>
                  <Typography sx={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', fontWeight: 700 }}>Duration</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>{exam.duration || 'N/A'} mins</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <QuizIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                <Box>
                  <Typography sx={{ color: '#94a3b8', fontSize: '10px', textTransform: 'uppercase', fontWeight: 700 }}>Questions</Typography>
                  <Typography sx={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>{exam.totalQuestions || 0}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {subjectList.length > 0 && (
            <Box sx={{ mb: '24px' }}>
              <Typography sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', mb: '8px', fontSize: '10px' }}>Subjects</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {subjectList.map((subject, idx) => (
                  <Chip key={idx} label={<LatexRenderer text={`${subject.name}: ${subject.questions} Qs`} />} size="small" variant="outlined" sx={{ borderRadius: '6px', bgcolor: subjectColor(subject.name).bg, borderColor: subjectColor(subject.name).border, color: subjectColor(subject.name).fg, fontWeight: 600, fontSize: '11px' }} />
                ))}
              </Box>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            onClick={() => onStartExamClick && onStartExamClick(exam)}
            endIcon={<ArrowForwardIcon />}
            sx={{ bgcolor: '#1d4ed8', borderRadius: '8px', py: '8px', fontWeight: 700, fontSize: '14px', textTransform: 'none', '&:hover': { bgcolor: '#1e40af' } }}
          >
            Start Exam
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>

      <Container maxWidth="lg" disableGutters sx={{ mx: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '40px', flexDirection: isMobile ? 'column' : 'row', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontWeight: 800, color: '#1a204d', fontSize: '32px', mb: '4px' }}>Scheduled Exams</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '16px' }}>Exams assigned to you by your instructors.</Typography>
          </Box>
        </Box>

        {linkError && (
          <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: '24px', p: '12px 16px', borderRadius: '10px', bgcolor: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', fontSize: '14px' }}>
            <ErrorOutlineIcon sx={{ fontSize: 20 }} />
            {linkError}
          </Box>
        )}

        {loading && (
          <Box sx={{ textAlign: 'center', py: '80px' }}>
            <CircularProgress color="primary" />
            <Typography sx={{ mt: '16px', color: '#64748b', fontSize: '14px' }}>Loading scheduled exams...</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ textAlign: 'center', py: '80px', color: '#ef4444' }}>
            <ErrorOutlineIcon sx={{ fontSize: '48px', mb: '8px' }} />
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{error}</Typography>
          </Box>
        )}

        {!loading && !error && exams.length === 0 && (
          <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ p: '32px', pb: 0 }}>
              <Typography sx={{ fontWeight: 700, mb: '8px', fontSize: '22px', color: '#0f172a' }}>No exams scheduled yet</Typography>
              <Typography sx={{ fontSize: '15px', color: '#64748b' }}>When your instructor schedules an exam it will appear here. Meanwhile, take a practice exam.</Typography>
            </Box>

            <Box sx={{ p: '32px' }}>
              <Typography sx={{ fontWeight: 600, color: '#334155', mb: '16px', fontSize: '15px' }}>Practise for</Typography>
              <Grid container spacing={3} sx={{ mb: '40px' }}>
                {['JEE', 'NEET', 'KCET', 'PUC'].map((type) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={type}>
                    <Box component={Paper} elevation={0} onClick={onPracticeClick} sx={{
                      p: '16px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
                      transition: 'border-color 0.15s, background-color 0.15s', '&:hover': { borderColor: '#1d4ed8', bgcolor: '#f8fafc' }
                    }}>
                      <Box sx={{
                        width: '50px', height: '50px', borderRadius: '50%', bgcolor: '#eff6ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: '12px',
                        color: '#1d4ed8', fontWeight: 800, fontSize: '12px'
                      }}>{type}</Box>
                      <Typography sx={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>
                        {type === 'PUC' ? 'Boards' : `${type} Main`}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ mb: '32px' }} />

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  onClick={onPracticeClick}
                  startIcon={<CreateIcon />}
                  sx={{ bgcolor: '#1d4ed8', borderRadius: '10px', px: '48px', py: '12px', fontWeight: 700, fontSize: '16px', textTransform: 'none' }}
                >
                  Start a Practice Exam
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {!loading && !error && exams.length > 0 && (() => {
          const byType = examTypeFilter
            ? exams.filter(e => e.examType === examTypeFilter)
            : exams;
          const examSubjects = (exam) => getSubjectList(exam.subjects)
            .filter(sub => Number(sub.questions) > 0)
            .map(sub => sub.name);
          // Only subjects that the listed exams actually contain (plus the chosen one, so it can be cleared)
          const subjects = [...new Set([...byType.flatMap(examSubjects), ...(subjectFilter ? [subjectFilter] : [])])].sort();
          const filteredExams = subjectFilter
            ? byType.filter(e => examSubjects(e).includes(subjectFilter))
            : byType;

          const chipSx = (on) => ({
            fontWeight: 700,
            fontSize: '13px',
            bgcolor: on ? '#1d4ed8' : '#f1f5f9',
            color: on ? '#ffffff' : '#475569',
            '&:hover': { bgcolor: on ? '#1e40af' : '#e2e8f0' },
          });
          const filterRow = (label, key, current, options) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Typography sx={{ width: { xs: '100%', sm: '64px' }, fontSize: '12px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
              </Typography>
              <Chip label="All" onClick={() => setFilter(key, null)} sx={chipSx(!current)} />
              {options.map(option => (
                <Chip key={option} label={option} onClick={() => setFilter(key, current === option ? null : option)} sx={chipSx(current === option)} />
              ))}
            </Box>
          );

          return (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mb: '24px' }}>
                {filterRow('Exam', 'type', examTypeFilter, EXAM_TYPES)}
                {subjects.length > 0 && filterRow('Subject', 'subject', subjectFilter, subjects)}
              </Box>

              {filteredExams.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: '60px', color: '#94a3b8' }}>
                  <QuizIcon sx={{ fontSize: '48px', mb: '12px', opacity: 0.4 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                    No {[examTypeFilter, subjectFilter].filter(Boolean).join(' ')} exams scheduled.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                  gap: '24px'
                }}>
                  {filteredExams.map(exam => renderExamCard(exam))}
                </Box>
              )}
            </>
          );
        })()}
      </Container>

    </Box>
  );
}

export default Dashboard;

